import { Component } from '@theme/component';

/** Progressive enhancement for the native contact form; Shopify owns delivery and captcha. */
/** @extends {Component<{form?: HTMLFormElement, email?: HTMLInputElement, emailError?: HTMLElement, submit?: HTMLButtonElement, submitLabel?: HTMLElement, frequency?: HTMLSelectElement, success?: HTMLElement}>} */
class BusinessFlowers extends Component {
  #pending = false;
  #releaseTimer;
  #controller;
  #submitText = '';
  #revealedSuccess;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#prepare();
    window.addEventListener('pageshow', () => this.#reset(), { signal: this.#controller.signal });
    window.addEventListener('load', () => this.#revealSuccess(), { once: true, signal: this.#controller.signal });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#prepare();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    clearTimeout(this.#releaseTimer);
    this.#revealedSuccess = undefined;
  }

  #prepare() {
    if (this.refs.form) this.refs.form.noValidate = true;
    if (this.refs.submitLabel) this.#submitText = this.refs.submitLabel.textContent;
    this.#reset();
    if (document.readyState === 'complete') this.#revealSuccess();
  }

  #revealSuccess() {
    // This ref exists only when Shopify's Liquid form confirms a successful POST.
    const success = this.refs.success;
    if (!success || success === this.#revealedSuccess) return;
    this.#revealedSuccess = success;
    success.focus({ preventScroll: true });
    success.scrollIntoView({ block: 'center', behavior: 'instant' });
  }

  /** @param {MouseEvent} event - Native plan-anchor click; leave navigation intact. */
  chooseFrequency(event) {
    const link = event.target.closest('[data-frequency]');
    if (!link || !this.contains(link) || !this.refs.frequency) return;
    const value = link.dataset.frequency;
    if (['Weekly', 'Fortnightly', 'Monthly'].includes(value)) this.refs.frequency.value = value;
  }

  /** @param {SubmitEvent} event - Preserve Shopify's native form and captcha flow. */
  handleSubmit(event) {
    if (event.defaultPrevented || !this.refs.email) return;
    if (this.#pending) {
      event.preventDefault();
      return;
    }
    if (!this.refs.email.validity.valid) {
      event.preventDefault();
      const message = this.refs.email.validity.valueMissing ? this.dataset.emailRequired : this.dataset.emailInvalid;
      this.refs.email.setAttribute('aria-invalid', 'true');
      if (this.refs.emailError) {
        this.refs.emailError.textContent = message;
        this.refs.emailError.hidden = false;
      }
      this.refs.email.focus();
      return;
    }
    this.clearEmailError();
    // Captcha can cancel this event and request a fresh submission after verification.
    queueMicrotask(() => {
      if (event.defaultPrevented) return;
      this.#pending = true;
      this.refs.form?.setAttribute('aria-busy', 'true');
      if (this.refs.submit) this.refs.submit.disabled = true;
      if (this.refs.submitLabel) this.refs.submitLabel.textContent = this.dataset.sending;
      clearTimeout(this.#releaseTimer);
      this.#releaseTimer = setTimeout(() => this.#reset(), 15000);
    });
  }

  /** Clear validation feedback when the email changes. */
  clearEmailError() {
    if (!this.refs.email?.validity.valid) return;
    this.refs.email.removeAttribute('aria-invalid');
    if (this.refs.emailError) {
      this.refs.emailError.hidden = true;
      this.refs.emailError.textContent = '';
    }
  }

  #reset() {
    this.#pending = false;
    clearTimeout(this.#releaseTimer);
    this.refs.form?.removeAttribute('aria-busy');
    if (this.refs.submit) this.refs.submit.disabled = false;
    if (this.refs.submitLabel) this.refs.submitLabel.textContent = this.#submitText;
  }
}

if (!customElements.get('business-flowers')) customElements.define('business-flowers', BusinessFlowers);
